import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  CHAT_ATTACHMENT_ACCEPT,
  CHAT_ATTACHMENT_RULES_LABEL,
  validateChatAttachment,
} from "./chatAttachmentConfig";

interface ChatInputProps {
  onSendMessage: (message: string, file?: File | null) => Promise<void> | void;
  isSending?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  isSending = false,
}) => {
  const [currentMessage, setCurrentMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentMessage(e.target.value);
  };

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;

    if (!file) {
      clearSelectedFile();
      return;
    }

    const validationError = validateChatAttachment(file);

    if (validationError) {
      clearSelectedFile();
      toast.warn(validationError);
      return;
    }

    setSelectedFile(file);
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSendMessage = async () => {
    if (isSending || (!currentMessage.trim() && !selectedFile)) {
      return;
    }

    try {
      await Promise.resolve(onSendMessage(currentMessage, selectedFile));
      setCurrentMessage("");
      clearSelectedFile();
    } catch (error) {
      console.error("Unable to send chat message:", error);
    }
  };

  return (
    <div
      className="chat-input-container"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
      }}
    >
      {selectedFile ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
            padding: "0.5rem 0.75rem",
            border: "1px solid #d9dee3",
            borderRadius: "0.75rem",
            background: "#f8f9fa",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              minWidth: 0,
            }}
          >
            <i className="la la-paperclip" />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedFile.name}
            </span>
          </div>
          <button
            type="button"
            onClick={clearSelectedFile}
            disabled={isSending}
            className="btn btn-sm btn-outline-secondary"
          >
            Retirer
          </button>
        </div>
      ) : null}

      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={CHAT_ATTACHMENT_ACCEPT}
          onChange={handleSelectFile}
          style={{ display: "none" }}
        />

        <input
          type="text"
          value={currentMessage}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSendMessage();
            }
          }}
          placeholder="Type a message..."
          className="chat-input"
          disabled={isSending}
          style={{
            flex: 1,
            padding: "0.75rem 1rem",
            borderRadius: "999px",
            border: "1px solid #d9dee3",
          }}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSending}
          className="gp"
          style={{
            padding: "0.75rem",
            background: "#fff",
            border: "1px solid #d9dee3",
            borderRadius: "50%",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#fff4bf";
            e.currentTarget.style.color = "#ffd02a";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#fff";
            e.currentTarget.style.color = "";
          }}
        >
          <i className="la la-paperclip" />
        </button>

        <button
          type="button"
          onClick={() => void handleSendMessage()}
          disabled={isSending}
          className="gp"
          style={{
            width: "46px",
            height: "46px",
            padding: "0",
            background: "#fff",
            border: "1px solid #d9dee3",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#fff4bf";
            e.currentTarget.style.color = "#ffd02a";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#fff";
            e.currentTarget.style.color = "";
          }}
        >
          <i className="las la-paper-plane"></i>
        </button>
      </div>

      <small style={{ color: "#6b7280" }}>{CHAT_ATTACHMENT_RULES_LABEL}</small>
    </div>
  );
};

export default ChatInput;
